// @ts-nocheck
import { Stack, Title, Text as MantineText, Code, SimpleGrid } from '@mantine/core';
import { Card } from '../../../components/ui';
import { KanbanBoard, StatusTracker, ApprovalFlow } from '../../../components/advanced';
import { mockTasks, mockProjects, getUserById } from '../mockData';

export default function WorkflowPage() {
  // Prepare Kanban data
  const kanbanColumns = [
    { id: 'todo', title: 'To Do', color: 'gray' },
    { id: 'in-progress', title: 'In Progress', color: 'blue' },
    { id: 'review', title: 'Review', color: 'orange' },
    { id: 'done', title: 'Done', color: 'green' },
  ];

  const kanbanTasks = mockTasks.slice(0, 10).map((task) => ({
    id: task.id,
    title: task.title,
    description: task.description,
    columnId: task.status,
    assignee: task.assigneeId ? getUserById(task.assigneeId) : undefined,
    priority: task.priority,
    tags: task.tags,
  }));

  // Approval flow data
  const approvalSteps = [
    { id: '1', label: 'Submitted', status: 'completed', completedAt: new Date('2024-01-10') },
    { id: '2', label: 'Manager Review', status: 'completed', completedAt: new Date('2024-01-12') },
    { id: '3', label: 'Finance Approval', status: 'current', assignee: 'Oscar Martinez' },
    { id: '4', label: 'Final Approval', status: 'pending' },
  ];

  // Status tracker data
  const statusSteps = [
    { label: 'Order Placed', status: 'completed', timestamp: new Date('2024-01-10T10:00:00') },
    { label: 'Processing', status: 'completed', timestamp: new Date('2024-01-10T14:30:00') },
    { label: 'Shipped', status: 'current', timestamp: new Date('2024-01-11T09:15:00') },
    { label: 'Out for Delivery', status: 'pending' },
    { label: 'Delivered', status: 'pending' },
  ];

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Workflow Components</Title>
        <MantineText c="dimmed" size="sm">
          Components for managing workflows, approvals, and task tracking
        </MantineText>
      </Stack>

      {/* Kanban Board */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>KanbanBoard</Title>
            <MantineText size="sm" c="dimmed">
              Drag-and-drop task board for visualizing workflow stages
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Example:</MantineText>
            <KanbanBoard
              columns={kanbanColumns}
              tasks={kanbanTasks}
              onTaskMove={(taskId, fromColumn, toColumn) => {
                console.log(`Moved task ${taskId} from ${fromColumn} to ${toColumn}`);
              }}
              onTaskClick={(task) => console.log('Task clicked:', task)}
            />
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`const columns = [
  { id: 'todo', title: 'To Do', color: 'gray' },
  { id: 'in-progress', title: 'In Progress', color: 'blue' },
  { id: 'review', title: 'Review', color: 'orange' },
  { id: 'done', title: 'Done', color: 'green' },
];

const tasks = [
  {
    id: '1',
    title: 'Design homepage',
    description: 'Create new homepage design',
    columnId: 'in-progress',
    assignee: { name: 'John Doe', avatar: '/avatar.jpg' },
    priority: 'high',
    tags: ['design', 'ui'],
  },
  // ... more tasks
];

<KanbanBoard
  columns={columns}
  tasks={tasks}
  onTaskMove={(taskId, fromColumn, toColumn) => {
    // Handle task movement
  }}
  onTaskClick={(task) => console.log('Task clicked:', task)}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Key Props:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • columns - Array of kanban columns
              <br />
              • tasks - Array of tasks to display
              <br />
              • onTaskMove - Handler for drag-and-drop
              <br />
              • onTaskClick - Task click handler
              <br />
              • onAddTask - Add new task handler
              <br />
              • renderTask - Custom task renderer
            </MantineText>
          </Stack>
        </Stack>
      </Card>

      {/* Approval Flow */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ApprovalFlow</Title>
            <MantineText size="sm" c="dimmed">
              Multi-step approval process visualization
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Example:</MantineText>
            <ApprovalFlow
              steps={approvalSteps}
              onApprove={() => console.log('Approved')}
              onReject={() => console.log('Rejected')}
            />
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`const approvalSteps = [
  {
    id: '1',
    label: 'Submitted',
    status: 'completed',
    completedAt: new Date('2024-01-10'),
  },
  {
    id: '2',
    label: 'Manager Review',
    status: 'completed',
    completedAt: new Date('2024-01-12'),
  },
  {
    id: '3',
    label: 'Finance Approval',
    status: 'current',
    assignee: 'Oscar Martinez',
  },
  {
    id: '4',
    label: 'Final Approval',
    status: 'pending',
  },
];

<ApprovalFlow
  steps={approvalSteps}
  onApprove={() => console.log('Approved')}
  onReject={() => console.log('Rejected')}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Step Interface:</MantineText>
            <Code block language="typescript">
{`interface ApprovalStep {
  id: string;
  label: string;
  status: 'pending' | 'current' | 'completed' | 'rejected';
  assignee?: string;
  completedAt?: Date;
  comments?: string;
}`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Status Tracker */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>StatusTracker</Title>
            <MantineText size="sm" c="dimmed">
              Track progress through sequential steps
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Example:</MantineText>
            <StatusTracker steps={statusSteps} />
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`const steps = [
  {
    label: 'Order Placed',
    status: 'completed',
    timestamp: new Date('2024-01-10T10:00:00'),
  },
  {
    label: 'Processing',
    status: 'completed',
    timestamp: new Date('2024-01-10T14:30:00'),
  },
  {
    label: 'Shipped',
    status: 'current',
    timestamp: new Date('2024-01-11T09:15:00'),
  },
  {
    label: 'Out for Delivery',
    status: 'pending',
  },
  {
    label: 'Delivered',
    status: 'pending',
  },
];

<StatusTracker steps={steps} />`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <Stack gap="xs">
              <MantineText size="sm" fw={500}>Project Management</MantineText>
              <MantineText size="xs" c="dimmed">
                Use KanbanBoard to manage project tasks across different stages. Track team progress and identify bottlenecks.
              </MantineText>
            </Stack>

            <Stack gap="xs">
              <MantineText size="sm" fw={500}>Purchase Approvals</MantineText>
              <MantineText size="xs" c="dimmed">
                Use ApprovalFlow for procurement requests, expense reports, and budget approvals with multi-level review.
              </MantineText>
            </Stack>

            <Stack gap="xs">
              <MantineText size="sm" fw={500}>Order Tracking</MantineText>
              <MantineText size="xs" c="dimmed">
                Use StatusTracker for e-commerce orders, shipping updates, and delivery status with timestamps.
              </MantineText>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
